import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { InvestigationLead } from "../../../db/schema";
import { api } from "../api";

export type TextViewerHandle = { goToPage: (page: number) => void };

interface Props {
  investigationId?: string;
  fileId?: string;
  // Supplies the pages without an investigation (the Files library).
  loadPages?: () => Promise<{ page: number | null; text: string }[]>;
  initialPage?: number;
  leads?: InvestigationLead[];
  activeLeadId?: string | null;
  onHoverLeads?: (ids: string[]) => void;
  onLoadError?: () => void;
}

// Inline view for Word and plain-text documents. The server split the text
// into pseudo-pages at extraction time (the same page numbers the leads are
// anchored to), so each section here is one of those pages. Flagged quotes
// are highlighted by matching the lead's quote text within its page; there
// is no geometry for these files, unlike the PDF overlay.
const TextFileViewer = forwardRef<TextViewerHandle, Props>(({ investigationId, fileId, loadPages, initialPage, leads = [], activeLeadId, onHoverLeads, onLoadError }, ref) => {
  const [pages, setPages] = useState<{ page: number | null; text: string }[] | null>(null);
  const [failed, setFailed] = useState(false);
  const pageRefs = useRef<Record<number, HTMLElement | null>>({});

  useEffect(() => {
    let cancelled = false;
    const load = loadPages ? loadPages() : api.fileText(investigationId!, fileId!).then((r) => r.pages);
    load
      .then((p) => {
        if (!cancelled) setPages(p);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        onLoadError?.();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId, fileId]);

  const goToPage = (page: number) => pageRefs.current[page]?.scrollIntoView({ behavior: "smooth", block: "start" });
  useImperativeHandle(ref, () => ({ goToPage }));

  useEffect(() => {
    if (pages && initialPage) setTimeout(() => goToPage(initialPage), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, initialPage]);

  const activeLead = useMemo(() => leads.find((l) => l.id === activeLeadId) || null, [leads, activeLeadId]);
  useEffect(() => {
    if (activeLead?.page) goToPage(activeLead.page);
  }, [activeLead]);

  if (failed) {
    return <div className="p-6 text-base-content/70">The extracted text for this file isn't available.</div>;
  }
  if (!pages) {
    return <div className="p-6 animate-pulse space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-4 bg-base-300 rounded w-full" />)}</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {pages.map((p) => {
        const pageNo = p.page ?? 0;
        const pageLeads = leads.filter((l) => l.status !== "dismissed" && l.page === pageNo && l.quote);
        return (
          <section
            key={pageNo}
            ref={(el) => {
              pageRefs.current[pageNo] = el;
            }}
            className="rounded-lg border border-base-300 bg-white text-gray-900 shadow-sm"
          >
            <div className="px-4 py-1.5 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-2">
              Section {pageNo}
              {pageLeads.length > 0 && <span className="text-blue-600">· {pageLeads.length} flag{pageLeads.length === 1 ? "" : "s"}</span>}
            </div>
            <div className="px-4 py-3 text-[13.5px] leading-relaxed whitespace-pre-wrap font-serif">
              {renderWithHighlights(p.text, pageLeads, activeLeadId, onHoverLeads)}
            </div>
          </section>
        );
      })}
    </div>
  );
});
TextFileViewer.displayName = "TextFileViewer";

// Split the page text around each lead's quote (first match, case- and
// whitespace-insensitive) and wrap the match in a tinted span. Overlapping
// quotes are rare; the first lead to claim a span wins.
function renderWithHighlights(text: string, pageLeads: InvestigationLead[], activeLeadId: string | null | undefined, onHoverLeads?: (ids: string[]) => void) {
  type Span = { start: number; end: number; lead: InvestigationLead };
  const spans: Span[] = [];
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
  const lowered = text.toLowerCase().replace(/\s+/g, " ");
  // Map positions in the whitespace-collapsed string back to the original.
  const map: number[] = [];
  let collapsed = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      if (collapsed.endsWith(" ")) continue;
      collapsed += " ";
    } else collapsed += ch.toLowerCase();
    map.push(i);
  }
  void lowered;
  for (const lead of pageLeads) {
    const q = norm(lead.quote || "");
    if (q.length < 8) continue;
    const at = collapsed.indexOf(q);
    if (at < 0) continue;
    const start = map[at];
    const end = map[Math.min(at + q.length - 1, map.length - 1)] + 1;
    if (spans.some((s) => start < s.end && end > s.start)) continue;
    spans.push({ start, end, lead });
  }
  if (!spans.length) return text;
  spans.sort((a, b) => a.start - b.start);
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (const s of spans) {
    if (s.start > cursor) out.push(text.slice(cursor, s.start));
    const sev = s.lead.severity || 1;
    out.push(
      <mark
        key={s.lead.id}
        data-lead-id={s.lead.id}
        onMouseEnter={() => onHoverLeads?.([s.lead.id])}
        onMouseLeave={() => onHoverLeads?.([])}
        className={cn(
          "rounded-sm px-0.5 cursor-default transition-shadow",
          sev >= 4 ? "bg-red-200/80" : sev === 3 ? "bg-amber-200/80" : "bg-yellow-100",
          s.lead.id === activeLeadId && "ring-2 ring-blue-500"
        )}
      >
        {text.slice(s.start, s.end)}
      </mark>
    );
    cursor = s.end;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

export default TextFileViewer;
